import React, { useState, useEffect } from 'react';
import { Clock, Mail, CheckCircle, LogOut, Menu, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import logo from '../assets/logo/logo.svg';

const PendingApprovalDashboard = () => {
  const { user, logout } = useAuth();
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  useEffect(() => {
    if (showMobileMenu) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }
    return () => document.body.classList.remove('mobile-menu-open');
  }, [showMobileMenu]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setShowMobileMenu(false);
    };
    const handleResize = () => {
      if (window.innerWidth >= 1024) setShowMobileMenu(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const sidebarContent = (
    <>
      <div className="flex flex-col items-center justify-center h-16 lg:h-20 border-b border-secondary-200 bg-white px-4">
        <img
          src={logo}
          alt="Tick Sync Logo"
          className="h-12 w-auto max-w-[140px] object-contain my-1"
        />
      </div>

      <div className="p-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <div className="flex items-center gap-2 text-yellow-800">
            <Clock className="h-4 w-4" />
            <span className="text-sm font-medium">Pending Approval</span>
          </div>
          <p className="text-xs text-yellow-700 mt-1">
            Your account is under review
          </p>
        </div>
      </div>

      <div className="flex-1" />

      <div className="p-4 border-t border-secondary-200">
        <button
          onClick={logout}
          className="w-full flex items-center gap-2 px-3 py-3 min-h-[44px] text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        >
          <LogOut className="h-4 w-4" />
          <span className="text-sm">Sign Out</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="auth-page min-h-screen min-h-[100dvh] bg-secondary-50 flex">
      {showMobileMenu && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setShowMobileMenu(false)}
          aria-hidden="true"
        />
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:flex w-56 min-h-screen flex-col bg-white border-r border-secondary-200">
        {sidebarContent}
      </div>

      {/* Mobile slide-in sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-56 flex flex-col bg-white border-r border-secondary-200 transform transition-transform duration-300 ease-in-out lg:hidden ${
          showMobileMenu ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between p-3 border-b border-secondary-200">
          <span className="text-sm font-medium text-secondary-900">Menu</span>
          <button
            type="button"
            onClick={() => setShowMobileMenu(false)}
            className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-secondary-600"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {sidebarContent}
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-secondary-200 flex items-center h-16 lg:h-20 px-4 lg:px-6">
          <button
            type="button"
            onClick={() => setShowMobileMenu(true)}
            className="lg:hidden p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-secondary-600 hover:text-primary-600 mr-2"
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </button>
          <h1 className="text-base sm:text-lg lg:text-xl font-semibold text-secondary-900 truncate">
            Account Pending Approval
          </h1>
          <div className="flex items-center gap-2 sm:gap-4 ml-auto shrink-0">
            <div className="flex items-center gap-2 text-secondary-600 min-w-0">
              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center shrink-0">
                <span className="text-sm font-bold text-primary-600 uppercase">
                  {user?.full_name?.charAt(0) || user?.email?.charAt(0) || '?'}
                </span>
              </div>
              <span className="text-sm font-medium hidden sm:inline truncate max-w-[10rem]">
                {user?.full_name || user?.email}
              </span>
            </div>
            <button
              className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded hover:bg-red-50"
              onClick={logout}
              title="Logout"
              aria-label="Logout"
            >
              <LogOut className="h-5 w-5 text-red-500" />
            </button>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 flex items-center justify-center">
          <div className="max-w-lg w-full">
            <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-5 sm:p-8 text-center">
              <div className="mx-auto h-16 w-16 sm:h-20 sm:w-20 bg-yellow-100 rounded-full flex items-center justify-center mb-6">
                <Clock className="h-8 w-8 sm:h-10 sm:w-10 text-yellow-600" />
              </div>

              <h2 className="text-xl sm:text-2xl font-bold text-secondary-900 mb-2">
                Request Submitted Successfully
              </h2>
              <p className="text-secondary-600 mb-6 text-sm sm:text-base">
                Your request to join the platform has been sent to the administrator
              </p>

              <div className="bg-secondary-50 rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-3 mb-3">
                  <CheckCircle className="h-5 w-5 text-green-500 shrink-0" />
                  <span className="text-sm font-medium text-secondary-900">
                    Account Created
                  </span>
                </div>

                <div className="flex items-start space-x-3">
                  <Mail className="h-5 w-5 text-primary-600 mt-0.5 shrink-0" />
                  <div className="text-left min-w-0">
                    <p className="text-sm font-medium text-secondary-900 truncate">
                      {user?.full_name || 'User'}
                    </p>
                    <p className="text-sm text-secondary-600 break-all">{user?.email}</p>
                    {user?.oauth_provider && (
                      <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium mt-1">
                        {user.oauth_provider === 'google' ? 'Google' : 'Microsoft'} Account
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Clock className="h-5 w-5 text-yellow-600" />
                  <h3 className="text-sm font-medium text-yellow-800">
                    Pending Admin Approval
                  </h3>
                </div>
                <div className="text-sm text-yellow-700">
                  <p>
                    Your account is currently under review by an administrator.
                    You'll receive an email notification once your access has been approved.
                  </p>
                </div>
              </div>

              <div className="text-left mb-6">
                <h4 className="text-sm font-medium text-secondary-900 mb-3">What happens next?</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full shrink-0" />
                    <span className="text-sm text-secondary-600">Account created successfully</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse shrink-0" />
                    <span className="text-sm text-secondary-600">Admin reviewing your request</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-secondary-300 rounded-full shrink-0" />
                    <span className="text-sm text-secondary-400">Email notification sent</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-secondary-300 rounded-full shrink-0" />
                    <span className="text-sm text-secondary-400">Access granted</span>
                  </div>
                </div>
              </div>

              <div className="text-sm text-secondary-500 space-y-1">
                <p>This process usually takes 1-2 business days.</p>
                <p>You can close this page safely. We'll notify you via email when your account is ready.</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default PendingApprovalDashboard;
